import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Paper, Button, TablePagination, CircularProgress, TextField,
  Alert, Snackbar, Tabs, Tab,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { SEARCE_COLORS } from '../theme';
import { assetsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { AssetResponse, SnackbarState, ColumnFilterMenuState } from '../types/assets';
import {
  getCellValue, applySearchAndColumnFilters, sortAssetsByColumn,
} from '../utils/assetHelpers';
import AssetDetailModal from '../components/searce-assets/AssetDetailModal';
import SubmitAssetDialog from '../components/searce-assets/SubmitAssetDialog';
import AssetAiChatDialog from '../components/searce-assets/AssetAiChatDialog';
import AssetTypeFilter, { type AssetTypeFilterValue } from '../components/searce-assets/AssetTypeFilter';
import AssetMarketplaceFilter, { type AssetMarketplaceFilterValue } from '../components/searce-assets/AssetMarketplaceFilter';
import AssetViewToggle, { type AssetViewMode } from '../components/searce-assets/AssetViewToggle';
import AssetTable from '../components/searce-assets/AssetTable';
import AssetCardGrid from '../components/searce-assets/AssetCardGrid';
import { PageHeader } from '../components/customerSentiment/PageHeader';

// ============================================================================
// COMPONENT
// ============================================================================

const SearceAssetsPage: React.FC = () => {
  const { user } = useAuth();
  const currentEmail = user?.email?.toLowerCase() ?? '';

  // ----- Tab state -----
  const [activeTab, setActiveTab] = useState(0);

  // ----- View / type / marketplace filters -----
  const [viewMode, setViewMode] = useState<AssetViewMode>('grid');
  const [assetTypeFilter, setAssetTypeFilter] = useState<AssetTypeFilterValue>('all');
  const [marketplaceFilter, setMarketplaceFilter] = useState<AssetMarketplaceFilterValue>('all');

  // ----- Table state -----
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [filterMenus, setFilterMenus] = useState<Record<string, ColumnFilterMenuState>>({});
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedAsset, setSelectedAsset] = useState<AssetResponse | null>(null);
  /** When set with column filter on asset_code, table rows follow this order until cleared or user edits Asset ID filter */
  const [aiChatAssetOrder, setAiChatAssetOrder] = useState<string[] | null>(null);

  // ----- Dialog state -----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });

  // ----- Data fetching -----
  const { data, isLoading, error: fetchError } = useQuery({
    queryKey: ['searce-assets'],
    queryFn: () => assetsApi.listAssets(),
  });

  const { data: mySbuData, isLoading: mySbuLoading, error: mySbuError } = useQuery({
    queryKey: ['searce-assets', 'my-sbu'],
    queryFn: () => assetsApi.listAssets('my_sbu'),
    staleTime: 2 * 60 * 1000,
  });

  const allAssets = data?.assets ?? [];
  const mySbuAssets = mySbuData?.assets ?? [];

  const myAssets = useMemo(
    () => allAssets.filter(a => a.team_members.some(e => e.toLowerCase() === currentEmail)),
    [allAssets, currentEmail],
  );

  const tabAssets = activeTab === 0 ? allAssets : activeTab === 1 ? mySbuAssets : myAssets;
  const tabLoading = activeTab === 1 ? mySbuLoading : isLoading;
  const tabError = activeTab === 1 ? mySbuError : fetchError;

  const assets = useMemo(() => {
    let next = tabAssets;
    if (assetTypeFilter !== 'all') {
      next = next.filter(a => a.asset_type === assetTypeFilter);
    }
    if (marketplaceFilter === 'listed') {
      next = next.filter(a => Boolean(a.marketplace_listing_status));
    } else if (marketplaceFilter === 'not_listed') {
      next = next.filter(a => !a.marketplace_listing_status);
    }
    return next;
  }, [tabAssets, assetTypeFilter, marketplaceFilter]);

  // =========================================================================
  // FILTER LOGIC
  // =========================================================================

  const getUniqueValuesForColumn = useCallback((columnId: string): string[] => {
    const base = applySearchAndColumnFilters(assets, search, filterMenus, columnId);
    const set = new Set<string>();
    if (columnId === 'key_cloud_services') {
      base.forEach(row => {
        row.key_cloud_services.forEach(s => {
          if (s?.trim()) set.add(s.trim());
        });
      });
      return Array.from(set).sort();
    }
    base.forEach(row => {
      const val = getCellValue(row, columnId);
      if (val) set.add(val);
    });
    return Array.from(set).sort();
  }, [assets, search, filterMenus]);

  const handleFilterClick = (columnId: string, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setFilterMenus(prev => ({
      ...prev,
      [columnId]: { anchorEl: event.currentTarget, selectedValues: prev[columnId]?.selectedValues ?? [] },
    }));
  };

  const handleFilterClose = (columnId: string) => {
    setFilterMenus(prev => ({ ...prev, [columnId]: { ...prev[columnId], anchorEl: null } }));
  };

  const handleFilterSelect = (columnId: string, selectedValues: string[]) => {
    if (columnId === 'asset_code') {
      setAiChatAssetOrder(null);
    }
    setFilterMenus(prev => ({ ...prev, [columnId]: { ...prev[columnId], selectedValues, anchorEl: null } }));
    setPage(0);
  };

  const handleClearAllFilters = () => {
    setSearch('');
    setFilterMenus({});
    setAiChatAssetOrder(null);
    setOrderBy(null);
    setOrder('asc');
    setAssetTypeFilter('all');
    setMarketplaceFilter('all');
    setPage(0);
  };

  const hasAnyFilterActive = (): boolean => {
    if (search.trim()) return true;
    if (orderBy !== null) return true;
    if (assetTypeFilter !== 'all') return true;
    if (marketplaceFilter !== 'all') return true;
    return Object.values(filterMenus).some(m => m?.selectedValues?.length > 0);
  };

  const filteredAssets = useMemo(
    () => applySearchAndColumnFilters(assets, search, filterMenus),
    [assets, search, filterMenus]
  );

  const sortedFilteredAssets = useMemo(() => {
    if (orderBy) {
      return sortAssetsByColumn(filteredAssets, orderBy, order);
    }
    if (aiChatAssetOrder?.length) {
      const idx = new Map(aiChatAssetOrder.map((id, i) => [id, i]));
      return [...filteredAssets].sort((a, b) => {
        const ia = idx.get(a.asset_code ?? '') ?? Number.MAX_SAFE_INTEGER;
        const ib = idx.get(b.asset_code ?? '') ?? Number.MAX_SAFE_INTEGER;
        return ia - ib;
      });
    }
    return filteredAssets;
  }, [filteredAssets, orderBy, order, aiChatAssetOrder]);

  const handleRequestSort = (columnId: string) => {
    if (orderBy === columnId) {
      setOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(columnId);
      setOrder('asc');
    }
    setPage(0);
  };

  const paginatedAssets = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedFilteredAssets.slice(start, start + rowsPerPage);
  }, [sortedFilteredAssets, page, rowsPerPage]);

  const findAssetByCode = useCallback(
    (code: string) =>
      allAssets.find(a => a.asset_code === code)
      ?? mySbuAssets.find(a => a.asset_code === code)
      ?? myAssets.find(a => a.asset_code === code),
    [allAssets, mySbuAssets, myAssets],
  );

  const handleOpenAssetFromChat = useCallback(
    (assetCode: string) => {
      const found = findAssetByCode(assetCode);
      if (found) {
        setSelectedAsset(found);
      } else {
        setSnackbar({
          open: true,
          message: `Asset ${assetCode} is not in the loaded lists. Try the "All Assets" tab and refresh.`,
          severity: 'error',
        });
      }
    },
    [findAssetByCode],
  );

  const handleAiSuggestedAssetIds = useCallback((assetIdsInOrder: string[]) => {
    setAiChatAssetOrder(assetIdsInOrder);
    setFilterMenus(prev => ({
      ...prev,
      asset_code: { anchorEl: null, selectedValues: assetIdsInOrder },
    }));
    setPage(0);
  }, []);

  const emptyMessage = tabAssets.length === 0
    ? activeTab === 0
      ? 'No assets submitted yet.'
      : activeTab === 1
        ? 'No assets with a teammate in your Sub-BU (happier data), or your Sub-BU could not be resolved.'
        : 'You are not listed as a team member on any asset yet.'
    : 'No results match your search.';

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 0, pb: 3 }}>
      <PageHeader
        title="Searce Assets"
        subtitle="This is where Searce's collective brainpower lives. If you've built a reusable IP, accelerator, or successful POC — whether it speeds up delivery or becomes a client value-add — submit it here. Even a small utility or repeatable framework can save someone's sprint. Every submission counts toward your performance. Only ask: keep it proven and reusable."
        actionButton={(
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{
              backgroundColor: SEARCE_COLORS.primary,
              color: SEARCE_COLORS.white,
              textTransform: 'none',
              fontWeight: 600,
              marginTop : 5,
              px: 3,
              py: 1,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              '&:hover': {
                backgroundColor: SEARCE_COLORS.primaryDark || SEARCE_COLORS.secondary,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              },
            }}
          >
            Submit Asset
          </Button>
        )}
      />

      {/* ---- Tabs ---- */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => {
            setActiveTab(v);
            setPage(0);
            setSearch('');
            setFilterMenus({});
            setAiChatAssetOrder(null);
            setOrderBy(null);
            setOrder('asc');
            setAssetTypeFilter('all');
            setMarketplaceFilter('all');
          }}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label={`All Assets (${allAssets.length})`} />
          <Tab label={`My SBU Assets (${mySbuAssets.length})`} />
          <Tab label={`My Assets (${myAssets.length})`} />
        </Tabs>
      </Paper>

      <Paper elevation={3} sx={{ p: 4, borderRadius: '16px' }}>

        {/* ---- Type filter + Search + View toggle ---- */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <AssetTypeFilter
            value={assetTypeFilter}
            onChange={value => {
              setAssetTypeFilter(value);
              setPage(0);
            }}
          />

          <AssetMarketplaceFilter
            value={marketplaceFilter}
            onChange={value => {
              setMarketplaceFilter(value);
              setPage(0);
            }}
          />

          <TextField
            size="small"
            placeholder="Search by asset ID, name, type, category, submitter, BU, SBU, cloud service..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '1.1rem' }} /> }}
            sx={{
              minWidth: 340,
              flexGrow: 1,
              maxWidth: 480,
              '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: '0.775rem' },
              '& .MuiInputBase-input': { fontSize: '0.775rem' },
            }}
          />

          {hasAnyFilterActive() && (
            <Button size="small" variant="outlined" onClick={handleClearAllFilters} sx={{ fontSize: '0.775rem' }}>
              Clear All Filters
            </Button>
          )}

          <Box sx={{ ml: 'auto' }}>
            <AssetViewToggle value={viewMode} onChange={setViewMode} />
          </Box>
        </Box>

        {/* ---- List / Grid ---- */}
        {tabLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : tabError ? (
          <Alert severity="error" sx={{ mb: 2 }}>Failed to load assets. Please try again.</Alert>
        ) : (
          <>
            {viewMode === 'list' ? (
              <AssetTable
                paginatedAssets={paginatedAssets}
                totalFilteredCount={sortedFilteredAssets.length}
                emptyMessage={emptyMessage}
                filterMenus={filterMenus}
                getUniqueValuesForColumn={getUniqueValuesForColumn}
                onFilterClick={handleFilterClick}
                onFilterClose={handleFilterClose}
                onFilterSelect={handleFilterSelect}
                onRequestSort={handleRequestSort}
                onAssetClick={setSelectedAsset}
              />
            ) : (
              <AssetCardGrid
                assets={paginatedAssets}
                emptyMessage={emptyMessage}
                onAssetClick={setSelectedAsset}
              />
            )}

            <TablePagination
              component="div"
              count={sortedFilteredAssets.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: '1px solid', borderColor: 'divider',
                fontSize: '0.775rem',
                '& .MuiTablePagination-toolbar': { fontSize: '0.775rem', minHeight: 48 },
                '& .MuiTablePagination-select': { color: SEARCE_COLORS.primary, fontWeight: 600 },
                '& .MuiIconButton-root': { color: SEARCE_COLORS.primary },
              }}
            />
          </>
        )}
      </Paper>

      {/* ---- Asset Detail Modal ---- */}
      <AssetDetailModal asset={selectedAsset} onClose={() => setSelectedAsset(null)} />

      <AssetAiChatDialog
        onOpenAssetByCode={handleOpenAssetFromChat}
        onAiSuggestedAssetIds={handleAiSuggestedAssetIds}
      />

      {/* ---- Submit Asset Dialog ---- */}
      <SubmitAssetDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSnackbar={setSnackbar}
      />

      {/* ---- Snackbar ---- */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SearceAssetsPage;
